/* eslint-env browser */
// Portal login (Q20): one orange "Log in", "Use OTP instead" as text,
// "Forgot password?" as a small link; real email / password / phone / code
// fields with autocomplete; errors under the field they are about; the
// reCAPTCHA script loads only when Sign up opens; no demo credentials.
import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useSelector, useDispatch, useStore } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GoogleLogin } from '@react-oauth/google';
import { FirebaseContext, api } from 'common';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import Header from 'components/Header/Header.js';
import HeaderLinks from 'components/Header/HeaderLinks.js';
import Footer from 'components/Footer/Footer.js';
import CountrySelect from '../components/CountrySelect';
import { InlineMessage, showToast } from '../components/feedback';
import { tokens as c } from '../theme/tokens';
import oromiaLogo from '../assets/img/oromia-bank.png';
import { processApiResponse } from '../utils/securityUtils';

// reCAPTCHA v3 site key (sign-up only).
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI';

const EMAIL_RE = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

// Oromia Bank's co-branded page keeps its green, darkened so white words read (5.0:1).
const OROMIA_GREEN = '#4E7C1E';
const OROMIA_GREEN_DARK = '#3F6618';

// The national part of a phone number: digits only, without the country
// code if it was typed, without the leading 0.
const nationalNumber = (raw, countryCode) => {
  let digits = String(raw || '').replace(/\D/g, '');
  const cc = String(countryCode || '');
  if (cc && digits.startsWith(cc) && digits.length - cc.length >= 7) digits = digits.substring(cc.length);
  if (digits.startsWith('0')) digits = digits.substring(1);
  return digits;
};

// Google and phone-code sign-ins show the app's loading screen, which
// unmounts this page; the step in flight is kept here so the error still
// shows when the page comes back.
let attemptInFlight = null;

const rawErrorText = (err) => {
  if (!err) return '';
  if (typeof err === 'string') return err;
  return `${err.code || ''} ${err.message || ''}`;
};

export default function LoginPage(props) {
  const { authRef, RecaptchaVerifier, signInWithPhoneNumber } = useContext(FirebaseContext);
  const { t } = useTranslation();
  const {
    googleLogin,
    clearLoginError,
    mobileSignIn,
    countries,
    sendResetMail,
    verifyEmailPassword,
    requestMobileOtp,
    verifyMobileOtp,
    mainSignUp,
    checkUserExists,
    validateReferer
  } = api;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const store = useStore();
  const auth = useSelector((state) => state.auth);
  const settings = useSelector((state) => state.settingsdata.settings) || {};
  const defaultPack = useSelector((state) => state.languagedata.defaultLanguage);
  const isOromia = props.path === '/oromia-bank';
  // Email and password always work here, whatever settings.emailLogin says:
  // staff sign in to the portal that way (the old page let any typed email
  // through as well). The phone code is offered when mobile login is on.
  const allowMobile = settings.mobileLogin === true;
  const appName = settings.appName || 'Donkey Drive';

  const [mode, setMode] = useState('password');
  const [isLoading, setIsLoading] = useState(false);
  const [verifier, setVerifier] = useState(null);
  const [mobileWithoutCountry, setMobileWithoutCountry] = useState('');
  const [showSignUp, setShowSignUp] = useState(false);
  const [message, setMessage] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const [loginPassword, setLoginPassword] = useState('');
  const [phoneSent, setPhoneSent] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [signupErrors, setSignupErrors] = useState({});
  const attemptRef = useRef(null); // 'password' | 'phone' | 'code' | 'google' while a login is in flight

  const [data, setData] = useState({
    email: '',
    confirmpassword: '',
    country: '',
    contact: '',
    mobile: '',
    password: '',
    otp: '',
    verificationId: null,
    firstName: '',
    lastName: '',
    selectedcountry: null,
    usertype: 'driver',
    tin: '',
    referralId: '',
    entryType: null
  });

  const { ...rest } = props;

  useEffect(() => {
    document.title = `${t('log_in', 'Log in')} · ${appName}`;
  }, [t, appName]);

  // The settings' country is the default for phone numbers.
  useEffect(() => {
    if (!settings.country || !Array.isArray(countries)) return;
    const found = countries.find((cn) => cn.label === settings.country);
    if (found) setData((d) => ({ ...d, country: found.phone, selectedcountry: found }));
  }, [settings.country, countries]);

  // reCAPTCHA v3 protects sign-up only, so its script loads when Sign up opens.
  useEffect(() => {
    if (!showSignUp) return undefined;
    const existing = document.getElementById('recaptcha-v3-script');
    if (existing) {
      if (window.grecaptcha) {
        setRecaptchaLoaded(true);
        return undefined;
      }
      const onLoad = () => setRecaptchaLoaded(true);
      existing.addEventListener('load', onLoad);
      return () => existing.removeEventListener('load', onLoad);
    }
    const script = document.createElement('script');
    script.id = 'recaptcha-v3-script';
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;
    script.onload = () => setRecaptchaLoaded(true);
    document.head.appendChild(script);
    return undefined;
  }, [showSignUp]);

  // Signed in: go to the role's first page.
  useEffect(() => {
    if (auth.profile && auth.profile.uid) {
      attemptInFlight = null;
      const role = auth.profile.usertype;
      if (role === 'admin' || role === 'fleetadmin') {
        navigate('/dashboard');
      } else if (role === 'driver') {
        navigate('/bookings');
      } else if (role === 'scout') {
        navigate('/onboard-driver');
      } else {
        navigate('/');
      }
    }
  }, [auth.profile, navigate]);

  const loginErrorText = useCallback(
    (err, step) => {
      if (!err) return null;
      const msg = typeof err === 'string' ? err : err.message;
      const notLoggedIn = [t('not_logged_in'), defaultPack && defaultPack.not_logged_in].filter(Boolean);
      if (msg && notLoggedIn.indexOf(msg) >= 0) return null; // the "nobody signed in" state, not an error
      const raw = rawErrorText(err);
      if (msg && msg === t('require_approval')) return t('require_approval');
      if (/too-many-requests/.test(raw)) return t('login_too_many_tries', 'Too many tries. Wait a few minutes, then try again.');
      if (/network-request-failed/.test(raw)) return t('no_internet_connection');
      if (/invalid-verification-code|invalid-code|code-expired/.test(raw) || step === 'code') return t('otp_validate_error');
      if (/wrong-password|invalid-credential|invalid-login-credentials|user-not-found|invalid-password/i.test(raw)) {
        return t('login_wrong_password', 'Wrong email or password. Try again or reset your password.');
      }
      if (/invalid-email/.test(raw)) return t('proper_email');
      if (/invalid-phone-number|missing-phone-number/.test(raw)) return t('mobile_no_blank_error');
      if (/user-disabled/.test(raw)) return t('user_issue_contact_admin');
      if (typeof err === 'string' && err.trim()) return err;
      return t('login_error');
    },
    [t, defaultPack]
  );

  // A failed login shows its reason under the field it is about.
  useEffect(() => {
    const step = attemptRef.current || attemptInFlight;
    if (!step || !auth.error || !auth.error.flag) return;
    const text = loginErrorText(auth.error.msg, step);
    if (!text) return;
    // After a remount the field it was about may not be on screen. Problems
    // with the account or the connection (approval needed, no profile,
    // offline) are not about a field, so they show above the form.
    const remounted = !attemptRef.current;
    const accountLevel = [t('require_approval'), t('user_issue_contact_admin'), t('no_internet_connection')].indexOf(text) >= 0;
    attemptRef.current = null;
    attemptInFlight = null;
    setIsLoading(false);
    const field = remounted || accountLevel ? 'form' : step === 'code' ? 'otp' : step === 'password' ? 'password' : step === 'phone' ? 'contact' : 'form';
    setFieldErrors({ [field]: text });
  }, [auth.error, loginErrorText, t]);

  // Never spin for ever if no answer comes back.
  useEffect(() => {
    if (!isLoading) return undefined;
    const timer = setTimeout(() => setIsLoading(false), 30000);
    return () => clearTimeout(timer);
  }, [isLoading]);

  const startAttempt = (step) => {
    if (auth.error && auth.error.flag) dispatch(clearLoginError());
    attemptRef.current = step;
    attemptInFlight = step;
    setFieldErrors({});
    setIsLoading(true);
  };

  const onInputChange = (event) => {
    const { id, value } = event.target;
    setData((d) => ({ ...d, [id]: value }));
    if (fieldErrors[id] || fieldErrors.form) setFieldErrors({});
    if (signupErrors[id] || signupErrors.form) setSignupErrors((e) => ({ ...e, [id]: undefined, form: undefined }));
  };

  const switchMode = (next) => {
    setMode(next);
    setFieldErrors({});
    setLoginPassword('');
    setData((d) => ({ ...d, contact: '', otp: '', verificationId: null }));
  };

  const handleGoogleLogin = (credentialResponse) => {
    if (credentialResponse && credentialResponse.credential) {
      startAttempt('google');
      dispatch(googleLogin(credentialResponse.credential, null));
    } else {
      setFieldErrors({ form: t('login_error') });
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    const email = (data.contact || '').trim();
    const errs = {};
    if (!EMAIL_RE.test(email)) errs.contact = t('proper_email');
    if (!loginPassword) errs.password = t('login_password_required', 'Enter your password.');
    if (Object.keys(errs).length) {
      setFieldErrors(errs);
      return;
    }
    startAttempt('password');
    await dispatch(verifyEmailPassword(email, loginPassword));
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    if (!data.country) {
      setFieldErrors({ contact: t('country_blank_error') });
      return;
    }
    if (!settings.AllowCriticalEditsAdmin) {
      setFieldErrors({ contact: t('in_demo_mobile_login') });
      return;
    }
    const national = nationalNumber(data.contact, data.country);
    if (national.length < 7) {
      setFieldErrors({ contact: t('mobile_no_blank_error') });
      return;
    }
    const phoneNumber = `+${data.country}${national}`;
    startAttempt('phone');
    if (settings.customMobileOTP) {
      // Start clean, so the answer below is this request's own.
      dispatch(clearLoginError());
      await dispatch(requestMobileOtp(phoneNumber));
      const after = store.getState().auth;
      attemptRef.current = null;
      attemptInFlight = null;
      setIsLoading(false);
      if (after.error && after.error.flag) {
        setFieldErrors({ contact: loginErrorText(after.error.msg, 'phone') || t('sms_send_failed') });
        return;
      }
      if (!after.verificationId) {
        // No answer from the server (connection lost): no code was sent.
        setFieldErrors({ contact: t('sms_send_failed') });
        return;
      }
      setPhoneSent(phoneNumber);
      setData((d) => ({ ...d, verificationId: true }));
      return;
    }
    try {
      if (!window.recaptchaVerifier || verifier === null) {
        window.recaptchaVerifier = await new RecaptchaVerifier(authRef(), 'sign-in-button', { size: 'invisible' });
      }
      const appVerifier = window.recaptchaVerifier;
      setVerifier(appVerifier);
      const res = await signInWithPhoneNumber(authRef(), phoneNumber, appVerifier);
      setPhoneSent(phoneNumber);
      setData((d) => ({ ...d, verificationId: res.verificationId }));
    } catch (error) {
      setFieldErrors({ contact: loginErrorText(error, 'phone') });
    }
    // A used verifier cannot be used again; the next try makes a new one.
    try {
      if (window.recaptchaVerifier) window.recaptchaVerifier.clear();
    } catch (err) {
      // already cleared
    }
    window.recaptchaVerifier = null;
    setVerifier(null);
    attemptRef.current = null;
    attemptInFlight = null;
    setIsLoading(false);
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    const code = String(data.otp || '').replace(/\D/g, '');
    if (code.length !== 6) {
      setFieldErrors({ otp: t('otp_validate_error') });
      return;
    }
    startAttempt('code');
    if (settings.customMobileOTP) {
      dispatch(verifyMobileOtp(phoneSent, code));
    } else {
      await dispatch(mobileSignIn(data.verificationId, code));
    }
  };

  const handleCancelCode = () => {
    attemptRef.current = null;
    attemptInFlight = null;
    setFieldErrors({});
    setPhoneSent('');
    setData((d) => ({ ...d, contact: '', otp: '', verificationId: null }));
    setIsLoading(false);
  };

  const forgotPassword = async () => {
    const email = (data.contact || '').trim();
    if (!EMAIL_RE.test(email)) {
      setFieldErrors({ contact: t('proper_email') });
      return;
    }
    await dispatch(sendResetMail(email));
    showToast({
      message: t('login_reset_sent', { email, defaultValue: 'Check {{email}} for a link to reset your password.' }),
      kind: 'success'
    });
  };

  const onCountryChange = (object, value) => {
    if (value && value.phone) {
      setData((d) => ({ ...d, country: value.phone, selectedcountry: value }));
      setMobileWithoutCountry('');
    }
  };

  const handleShowSignup = () => {
    setData((d) => ({ ...d, email: '', firstName: '', lastName: '', password: '', confirmpassword: '', usertype: 'driver', referralId: '', tin: '' }));
    setMobileWithoutCountry('');
    setSignupErrors({});
    setFieldErrors({});
    setShowSignUp((v) => !v);
  };

  const mobileInputChange = (e) => {
    const val = e.target.value;
    setMobileWithoutCountry(val);
    // Saved as plain digits after the country code: no spaces, dashes or
    // brackets, no leading 0, and no country code typed a second time.
    setData((d) => ({ ...d, mobile: `+${d.country}${nationalNumber(val, d.country)}` }));
    if (signupErrors.mobile) setSignupErrors((er) => ({ ...er, mobile: undefined }));
  };

  const handleChangeUser = (event) => {
    setData((d) => ({ ...d, usertype: event.target.value }));
  };

  const checkPasswordValidity = (value) => {
    if (value !== data.confirmpassword) {
      return t('confirm_password_not_match_err');
    }
    if (!/^\S*$/.test(value)) {
      return t('password_must_not_contain_whitespaces');
    }
    if (!/^(?=.*[A-Z]).*$/.test(value)) {
      return t('password_must_have_at_least_one_uppercase_character');
    }
    if (!/^(?=.*[a-z]).*$/.test(value)) {
      return t('password_must_have_at_least_one_lowercase_character');
    }
    if (!/^(?=.*[0-9]).*$/.test(value)) {
      return t('password_must_contain_at_least_one_digit');
    }
    if (!/^.{8,16}$/.test(value)) {
      return t('password_must_be_8-16_characters_long');
    }
    return null;
  };

  // Execute reCAPTCHA v3 verification
  const executeRecaptcha = useCallback(
    async (action) => {
      if (!recaptchaLoaded || !window.grecaptcha) {
        console.warn('reCAPTCHA not loaded yet');
        return null;
      }
      try {
        return await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action });
      } catch (error) {
        console.error('reCAPTCHA execution failed:', error);
        return null;
      }
    },
    [recaptchaLoaded]
  );

  const finishSignUp = () => {
    setData((d) => ({ ...d, email: '', firstName: '', lastName: '', password: '', confirmpassword: '', usertype: 'customer', referralId: '' }));
    setMobileWithoutCountry('');
    setMessage(true);
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    const errs = {};
    if (!(/\S/.test(data.firstName || ''))) errs.firstName = t('proper_input_name');
    if (!(/\S/.test(data.lastName || ''))) errs.lastName = t('proper_input_name');
    if (!EMAIL_RE.test(data.email || '')) errs.email = t('proper_email');
    if (nationalNumber(mobileWithoutCountry, data.country).length < 6) errs.mobile = t('mobile_no_blank_error');
    const passwordError = checkPasswordValidity(data.password || '');
    if (passwordError) {
      if (passwordError === t('confirm_password_not_match_err')) errs.confirmpassword = passwordError;
      else errs.password = passwordError;
    }
    if (Object.keys(errs).length) {
      setSignupErrors(errs);
      return;
    }
    setSignupErrors({});
    setIsLoading(true);
    try {
      // SECURITY: reCAPTCHA before sign-up
      const recaptchaToken = await executeRecaptcha('signup');
      if (!recaptchaToken) {
        setSignupErrors({ form: t('recaptcha_error', 'Security check failed. Please try again.') });
        return;
      }
      const userData = { ...data, recaptchaToken };
      if (userData.usertype === 'customer') delete userData.carType;
      delete userData.confirmpassword;
      delete userData.pass;
      const res = await checkUserExists(userData);
      // Handle encrypted response from server
      const decryptedRes = await processApiResponse(res);
      if (decryptedRes.users && decryptedRes.users.length > 0) {
        setSignupErrors({ form: t('user_exists') });
        return;
      }
      let signupData = userData;
      if (userData.referralId && userData.referralId.length > 0) {
        let referralInfo = null;
        try {
          referralInfo = await validateReferer(userData.referralId);
        } catch (err) {
          referralInfo = null;
        }
        if (!referralInfo || !referralInfo.uid) {
          setSignupErrors({ referralId: t('referer_not_found') });
          return;
        }
        signupData = { ...userData, signupViaReferral: referralInfo.uid };
      }
      delete signupData.country;
      const result = await mainSignUp(signupData);
      if (result && result.uid) {
        finishSignUp();
      } else {
        setSignupErrors({ form: (result && result.error) || t('reg_error') });
      }
    } catch (err) {
      setSignupErrors({ form: t('reg_error') });
    } finally {
      setIsLoading(false);
    }
  };

  const primarySx = isOromia
    ? { bgcolor: OROMIA_GREEN, '&:hover': { bgcolor: OROMIA_GREEN_DARK } }
    : {};
  const quietSx = isOromia ? { color: OROMIA_GREEN, '&:hover': { bgcolor: 'rgba(78,124,30,0.08)' } } : {};

  const busyLabel = (label) =>
    isLoading ? <CircularProgress size={22} sx={{ color: c.WHITE }} aria-label={t('loading', 'Loading…')} /> : label;

  const passwordToggle = (shown, toggle) => (
    <InputAdornment position="end">
      <IconButton
        onClick={toggle}
        edge="end"
        aria-label={shown ? t('hide_password', 'Hide password') : t('show_password', 'Show password')}
      >
        {shown ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
      </IconButton>
    </InputAdornment>
  );

  const showCountry = !isOromia && allowMobile && data.selectedcountry && Array.isArray(countries) && countries.length > 0;
  const codeSent = Boolean(data.verificationId);
  const emailLabel = isOromia ? t('privacy_policy_info_list3') : t('email', 'Email');

  const loginForm =
    mode === 'password' ? (
      <form noValidate onSubmit={handlePasswordLogin}>
        <TextField
          id="contact"
          type="email"
          label={emailLabel}
          value={data.contact}
          onChange={onInputChange}
          autoComplete="username"
          inputProps={{ inputMode: 'email', autoCapitalize: 'none', spellCheck: 'false' }}
          error={Boolean(fieldErrors.contact)}
          helperText={fieldErrors.contact || ' '}
          fullWidth
          margin="dense"
        />
        <TextField
          id="current-password"
          type={showPassword ? 'text' : 'password'}
          label={t('password', 'Password')}
          value={loginPassword}
          onChange={(e) => {
            setLoginPassword(e.target.value);
            if (fieldErrors.password || fieldErrors.form) setFieldErrors({});
          }}
          autoComplete="current-password"
          error={Boolean(fieldErrors.password)}
          helperText={fieldErrors.password || ' '}
          fullWidth
          margin="dense"
          InputProps={{ endAdornment: passwordToggle(showPassword, () => setShowPassword((v) => !v)) }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -0.5, mb: 1.5 }}>
          <Link component="button" type="button" onClick={forgotPassword} sx={{ fontSize: 14, minHeight: 44, ...quietSx }}>
            {t('forgot_password', 'Forgot password?')}
          </Link>
        </Box>
        <Button type="submit" variant="contained" fullWidth size="large" aria-busy={isLoading ? 'true' : 'false'} sx={{ minHeight: 48, ...primarySx }}>
          {busyLabel(t('log_in', 'Log in'))}
        </Button>
        {allowMobile ? (
          <Button variant="text" fullWidth onClick={() => switchMode('otp')} sx={{ mt: 1, minHeight: 44, ...quietSx }}>
            {t('login_use_otp', 'Use OTP instead')}
          </Button>
        ) : null}
      </form>
    ) : !codeSent ? (
      <form noValidate onSubmit={handleSendCode}>
        {showCountry ? (
          <Box sx={{ mt: 1, mb: 1 }}>
            <CountrySelect
              countries={countries}
              label={t('select_country')}
              value={data.selectedcountry}
              onChange={onCountryChange}
              disabled={codeSent}
              readOnly={!settings.AllowCountrySelection}
            />
          </Box>
        ) : null}
        <TextField
          id="contact"
          type="tel"
          label={t('phone', 'Phone')}
          value={data.contact}
          onChange={onInputChange}
          autoComplete="tel-national"
          inputProps={{ inputMode: 'numeric' }}
          InputProps={data.country ? { startAdornment: <InputAdornment position="start">{`+${data.country}`}</InputAdornment> } : undefined}
          error={Boolean(fieldErrors.contact)}
          helperText={fieldErrors.contact || ' '}
          fullWidth
          margin="dense"
        />
        <Button type="submit" variant="contained" fullWidth size="large" aria-busy={isLoading ? 'true' : 'false'} sx={{ mt: 1, minHeight: 48, ...primarySx }}>
          {busyLabel(t('login_send_code', 'Send code'))}
        </Button>
        <Button variant="text" fullWidth onClick={() => switchMode('password')} sx={{ mt: 1, minHeight: 44, ...quietSx }}>
          {t('login_use_password', 'Use email and password instead')}
        </Button>
      </form>
    ) : (
      <form noValidate onSubmit={handleVerifyCode}>
        <Typography variant="body2" sx={{ color: c.INK_2, mb: 1 }}>
          {t('login_code_sent', { phone: phoneSent, defaultValue: 'We sent a 6-digit code to {{phone}}.' })}
        </Typography>
        <TextField
          id="otp"
          label={t('login_code_label', 'Code')}
          value={data.otp}
          onChange={onInputChange}
          autoComplete="one-time-code"
          inputProps={{ inputMode: 'numeric', maxLength: 6, pattern: '[0-9]*', style: { letterSpacing: '0.3em', fontSize: 20 } }}
          error={Boolean(fieldErrors.otp)}
          helperText={fieldErrors.otp || ' '}
          fullWidth
          margin="dense"
          autoFocus
        />
        <Button type="submit" variant="contained" fullWidth size="large" aria-busy={isLoading ? 'true' : 'false'} sx={{ mt: 1, minHeight: 48, ...primarySx }}>
          {busyLabel(t('verify_otp', 'Verify OTP'))}
        </Button>
        <Button variant="text" fullWidth onClick={handleCancelCode} sx={{ mt: 1, minHeight: 44, ...quietSx }}>
          {t('cancel', 'Cancel')}
        </Button>
      </form>
    );

  const signUpForm = (
    <form noValidate onSubmit={handleSignUp}>
      <Typography component="h1" sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.35, mb: 2, color: c.INK }}>
        {t('signup', 'Sign up')}
      </Typography>
      {signupErrors.form ? (
        <InlineMessage kind="error" sx={{ mb: 2 }}>
          {signupErrors.form}
        </InlineMessage>
      ) : null}
      {message ? (
        <InlineMessage kind="success" sx={{ mb: 2 }}>
          {t('account_create_successfully')}
        </InlineMessage>
      ) : null}
      <Grid container columnSpacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            id="firstName"
            label={t('first_name')}
            value={data.firstName}
            onChange={onInputChange}
            autoComplete="given-name"
            required
            error={Boolean(signupErrors.firstName)}
            helperText={signupErrors.firstName || ' '}
            fullWidth
            margin="dense"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            id="lastName"
            label={t('last_name')}
            value={data.lastName}
            onChange={onInputChange}
            autoComplete="family-name"
            required
            error={Boolean(signupErrors.lastName)}
            helperText={signupErrors.lastName || ' '}
            fullWidth
            margin="dense"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          {data.selectedcountry && Array.isArray(countries) && countries.length > 0 ? (
            <Box sx={{ mt: 1, mb: 2.5 }}>
              <CountrySelect
                countries={countries}
                label={t('select_country')}
                value={data.selectedcountry}
                onChange={onCountryChange}
                disabled={Boolean(data.verificationId)}
                readOnly={!settings.AllowCountrySelection}
              />
            </Box>
          ) : null}
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            id="mobileWithoutCountry"
            type="tel"
            label={t('phone')}
            value={mobileWithoutCountry}
            onChange={mobileInputChange}
            autoComplete="tel-national"
            inputProps={{ inputMode: 'numeric' }}
            required
            error={Boolean(signupErrors.mobile)}
            helperText={signupErrors.mobile || ' '}
            fullWidth
            margin="dense"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth margin="dense">
            <InputLabel id="signup-usertype-label">{t('user_type', 'User type')}</InputLabel>
            <Select
              labelId="signup-usertype-label"
              id="signup-usertype"
              value={data.usertype || ''}
              label={t('user_type', 'User type')}
              onChange={handleChangeUser}
            >
              <MenuItem value="driver">{t('driver')}</MenuItem>
              <MenuItem value="customer">{t('customer')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            id="email"
            type="email"
            label={t('email')}
            value={data.email}
            onChange={onInputChange}
            autoComplete="email"
            inputProps={{ inputMode: 'email', autoCapitalize: 'none', spellCheck: 'false' }}
            required
            error={Boolean(signupErrors.email)}
            helperText={signupErrors.email || ' '}
            fullWidth
            margin="dense"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            id="password"
            type={showPassword ? 'text' : 'password'}
            label={t('password')}
            value={data.password}
            onChange={onInputChange}
            autoComplete="new-password"
            required
            error={Boolean(signupErrors.password)}
            helperText={signupErrors.password || ' '}
            fullWidth
            margin="dense"
            InputProps={{ endAdornment: passwordToggle(showPassword, () => setShowPassword((v) => !v)) }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            id="confirmpassword"
            type={showConfirmPassword ? 'text' : 'password'}
            label={t('confirm_password')}
            value={data.confirmpassword}
            onChange={onInputChange}
            autoComplete="new-password"
            required
            error={Boolean(signupErrors.confirmpassword)}
            helperText={signupErrors.confirmpassword || ' '}
            fullWidth
            margin="dense"
            InputProps={{ endAdornment: passwordToggle(showConfirmPassword, () => setShowConfirmPassword((v) => !v)) }}
          />
        </Grid>
        {data.usertype === 'driver' ? (
          <Grid item xs={12} md={6}>
            <TextField
              id="tin"
              label={t('tin')}
              value={data.tin}
              onChange={onInputChange}
              inputProps={{ inputMode: 'numeric' }}
              helperText=" "
              fullWidth
              margin="dense"
            />
          </Grid>
        ) : null}
        <Grid item xs={12} md={6}>
          <TextField
            id="referralId"
            label={t('referralId')}
            value={data.referralId}
            onChange={onInputChange}
            inputProps={{ autoCapitalize: 'characters', spellCheck: 'false' }}
            error={Boolean(signupErrors.referralId)}
            helperText={signupErrors.referralId || ' '}
            fullWidth
            margin="dense"
          />
        </Grid>
      </Grid>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row-reverse' }, gap: 1, mt: 1 }}>
        {message ? (
          <Button variant="contained" size="large" onClick={() => setShowSignUp(false)} sx={{ minHeight: 48, minWidth: 160, ...primarySx }}>
            {t('log_in', 'Log in')}
          </Button>
        ) : (
          <>
            <Button type="submit" variant="contained" size="large" aria-busy={isLoading ? 'true' : 'false'} sx={{ minHeight: 48, minWidth: 160, ...primarySx }}>
              {busyLabel(t('signup', 'Sign up'))}
            </Button>
            <Button variant="text" size="large" onClick={handleShowSignup} sx={{ minHeight: 48, ...quietSx }}>
              {t('log_in', 'Log in')}
            </Button>
          </>
        )}
      </Box>
    </form>
  );

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        '@supports (min-height: 100dvh)': { minHeight: '100dvh' },
        display: 'flex',
        flexDirection: 'column',
        overflowX: 'hidden',
        background: 'linear-gradient(135deg, #f1501a 0%, #ff6b35 50%, #ff8c42 100%)',
        '& footer, & footer a, & footer li a': { color: c.WHITE },
      }}
    >
      <CssBaseline />
      <Header absolute color="transparent" rightLinks={<HeaderLinks />} {...rest} />
      {/* Still circles, no motion: the page should feel calm. */}
      <Box aria-hidden="true" sx={{ position: 'absolute', width: 400, height: 400, borderRadius: '50%', background: 'rgba(255,255,255,0.10)', top: -100, left: 50 }} />
      <Box aria-hidden="true" sx={{ position: 'absolute', width: 300, height: 300, borderRadius: '50%', background: 'rgba(255,255,255,0.08)', bottom: -50, right: -50 }} />
      <Box aria-hidden="true" sx={{ position: 'absolute', width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', top: '50%', right: '10%' }} />

      <Container
        maxWidth={showSignUp ? 'md' : 'xs'}
        sx={{ position: 'relative', zIndex: 2, flex: 1, pt: { xs: '150px', md: '18vh' }, pb: { xs: 4, md: 6 }, px: { xs: 2, sm: 3 } }}
      >
        <div id="sign-in-button" />
        <Paper
          elevation={0}
          sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: '16px', border: 0, boxShadow: '0 12px 32px rgba(17,24,39,0.18)' }}
        >
          {isOromia ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <img src={oromiaLogo} alt="Oromia Bank" style={{ maxWidth: 200, height: 'auto' }} />
            </Box>
          ) : null}
          {showSignUp ? (
            signUpForm
          ) : (
            <>
              <Typography component="h1" sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.35, mb: 0.5, color: c.INK }}>
                {t('login', 'Log in')}
              </Typography>
              <Typography variant="body2" sx={{ color: c.INK_2, mb: 2 }}>
                {appName}
              </Typography>
              {fieldErrors.form ? (
                <InlineMessage kind="error" sx={{ mb: 2 }}>
                  {fieldErrors.form}
                </InlineMessage>
              ) : null}
              {loginForm}
              {settings.socialLogin ? (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <GoogleLogin onSuccess={handleGoogleLogin} onError={handleGoogleLogin} />
                  </Box>
                </>
              ) : null}
              <Divider sx={{ my: 2 }} />
              <Button variant="outlined" color="neutral" fullWidth onClick={handleShowSignup} disabled={isLoading} sx={{ minHeight: 44 }}>
                {t('signup', 'Sign up')}
              </Button>
            </>
          )}
        </Paper>

        {isOromia ? (
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <Box
              sx={{
                border: `5px solid #94c83d`,
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 4px 24px rgba(25, 118, 210, 0.12)',
                background: c.WHITE,
                width: '100%',
                maxWidth: 600,
                aspectRatio: '16 / 9',
              }}
            >
              <iframe
                src="https://iframe.mediadelivery.net/embed/489880/5acf6278-b9a9-46ec-bbc5-51ab04a65a76?autoplay=true&loop=false&muted=false&preload=true&responsive=true"
                width="100%"
                height="100%"
                style={{ border: 0, display: 'block' }}
                title="Donkey Drive - Onboarding Drivers Tutorial"
                loading="lazy"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                allowFullScreen
              />
            </Box>
          </Box>
        ) : null}
      </Container>
      <Box sx={{ position: 'relative', zIndex: 2 }}>
        <Footer whiteFont />
      </Box>
    </Box>
  );
}
